/**
 * src/domain/inventory/InventoryService.ts
 *
 * Dependency-Injected Domain Inventory Service with EventBus Integration,
 * Cryptographic COA Attachment, and Default-Deny Profile/Role Projections.
 */

import { EventBus, eventBus } from '../../core/events/EventBus';
import { InventoryRepository, PersistentInventoryRepository } from './PersistentInventoryRepository';
import { DomainProduct, ArtworkProduct, ICertificateOfAuthenticity, IProvenanceRecord, CertificateIssuer } from './types/product-types';
import { ProductFactory } from './ProductFactory';
import { COAVerifier } from './COAVerifier';
import { ProfileConfig } from '../../config/profiles/types';
import { publicPortalConfig } from '../../config/profiles/public-portal.config';
import { ProductProjection } from './ProductProjection';

export class InventoryService {
  private static instance: InventoryService;

  constructor(
    private repo: InventoryRepository = new PersistentInventoryRepository(),
    private bus: EventBus = eventBus
  ) {}

  public static getInstance(): InventoryService {
    if (!InventoryService.instance) {
      InventoryService.instance = new InventoryService();
    }
    return InventoryService.instance;
  }

  public async createArtwork(payload: Parameters<typeof ProductFactory.createArtwork>[0]): Promise<ArtworkProduct> {
    const artwork = ProductFactory.createArtwork(payload);
    await this.repo.save(artwork);
    return artwork;
  }

  public async updateArtwork(artwork: ArtworkProduct): Promise<ArtworkProduct> {
    await this.repo.save(artwork);
    return artwork;
  }

  public async attachCertificate(
    artworkId: string,
    cert: ICertificateOfAuthenticity,
    issuer: CertificateIssuer
  ): Promise<ArtworkProduct> {
    const product = (await this.repo.getRawInternal(artworkId)) || (await this.repo.get(artworkId));
    if (!product || product.type !== 'artwork') {
      throw new Error(`[InventoryService] Artwork '${artworkId}' not found`);
    }

    const isValid = COAVerifier.verifyCertificate(artworkId, product.tenantId, cert, issuer);
    if (!isValid) {
      throw new Error('[InventoryService] Cryptographic certificate verification failed');
    }

    // Immutable copy
    const updated: ArtworkProduct = {
      ...product,
      metadata: {
        ...product.metadata,
        certificateOfAuthenticity: {
          ...cert,
          isVerified: true,
        },
      },
    };

    await this.repo.save(updated);
    return updated;
  }

  public async addProvenance(artworkId: string, record: Omit<IProvenanceRecord, 'id' | 'timestamp'>): Promise<ArtworkProduct> {
    const product = (await this.repo.getRawInternal(artworkId)) || (await this.repo.get(artworkId));
    if (!product || product.type !== 'artwork') {
      throw new Error(`[InventoryService] Artwork '${artworkId}' not found`);
    }

    const newRecord: IProvenanceRecord = {
      ...record,
      id: `prov_${Date.now()}`,
      timestamp: new Date().toISOString(),
    };

    // Immutable copy
    const updated: ArtworkProduct = {
      ...product,
      metadata: {
        ...product.metadata,
        provenance: [...product.metadata.provenance, newRecord],
      },
    };

    await this.repo.save(updated);
    return updated;
  }

  public projectProductForRole(product: DomainProduct, role: string): DomainProduct {
    if (product.type !== 'artwork') return product;

    // Mask private prices for guest roles
    if (product.price.isPrivate && role === 'guest') {
      return {
        ...product,
        price: {
          isPrivate: true,
          amount: null,
          currency: product.price.currency,
          formatted: 'Price on Request',
        },
      };
    }

    return product;
  }

  public async listArtworks(roleOrProfile: string | ProfileConfig = 'guest'): Promise<ArtworkProduct[]> {
    if (typeof roleOrProfile === 'object') {
      const all = await this.repo.listProducts({ type: 'artwork' }, roleOrProfile);
      return all as ArtworkProduct[];
    }
    const all = await this.repo.list({ type: 'artwork' });
    return all.map((p) => this.projectProductForRole(p, roleOrProfile) as ArtworkProduct);
  }

  public async getProductById(id: string, roleOrProfile: string | ProfileConfig = 'guest'): Promise<DomainProduct | null> {
    if (typeof roleOrProfile === 'object') {
      return this.repo.getProduct(id, roleOrProfile);
    }
    const product = await this.repo.get(id);
    if (!product) return null;
    return this.projectProductForRole(product, roleOrProfile);
  }
}

export const inventoryService = InventoryService.getInstance();
